import { useQuery } from '@tanstack/react-query'
import { ArrowRight, CalendarClock, CircleSlash, History, UserCog, UserPlus } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { formatDateTime } from '@/lib/datetime'
import { queryKeys } from '@/lib/query-client'
import type { BookingChangeLogEntry } from '@/types/api'
import { Spinner } from '@/components/ui/Spinner'
import { fetchTripChangeLog } from './api'
import styles from './ChangeHistory.module.css'

interface ChangeHistoryProps {
  /** The trip whose booking history to show. Null renders nothing. */
  tripId: number | null
}

/**
 * Who changed this booking, what they changed it to, and why.
 *
 * Cancelling a confirmed trip and moving its times both used to leave nothing
 * behind but the new value. A customer saying "you told me nine and the driver
 * came at eleven" could not be answered from the record, because the record no
 * longer contained nine.
 *
 * Keyed on the trip but resolved to its booking server-side, so a change made
 * while the request was still pending — before this trip existed — appears here
 * too.
 */
export function ChangeHistory({ tripId }: ChangeHistoryProps) {
  const query = useQuery({
    queryKey: queryKeys.changeLog.forTrip(tripId ?? 0),
    queryFn: () => fetchTripChangeLog(tripId as number),
    enabled: tripId !== null,
  })

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>
        <History aria-hidden="true" />
        Change history
      </h3>

      {query.isLoading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : query.isError ? (
        <p className={styles.note} role="alert">
          {query.error instanceof ApiError ? query.error.message : 'The history could not be loaded.'}
        </p>
      ) : (query.data?.length ?? 0) === 0 ? (
        <p className={styles.note}>
          Nobody has changed this booking. Cancellations and time changes made from the dashboard are
          recorded here.
        </p>
      ) : (
        <ol className={styles.entries} role="list">
          {query.data?.map((entry) => (
            <Entry key={entry.id} entry={entry} />
          ))}
        </ol>
      )}
    </section>
  )
}

function Entry({ entry }: { entry: BookingChangeLogEntry }) {
  return (
    <li className={styles.entry}>
      <span className={styles.icon} aria-hidden="true">
        <ActionIcon action={entry.action} />
      </span>

      <div className={styles.detail}>
        <p className={styles.summary}>{entry.summary}</p>

        {/*
          The before and after in their own right, not only inside the sentence:
          an operator scanning for "what was the pickup time on Tuesday" reads
          the pair, not the prose.
        */}
        {entry.oldValue || entry.newValue ? (
          <p className={styles.change}>
            <span className={styles.old}>{entry.oldValue ?? '—'}</span>
            <ArrowRight aria-hidden="true" />
            <span className={styles.new}>{entry.newValue ?? '—'}</span>
          </p>
        ) : null}

        {entry.reason ? <p className={styles.reason}>“{entry.reason}”</p> : null}

        <p className={styles.meta}>
          {/*
            The name recorded at the time, not one looked up now — a renamed or
            disabled account does not rewrite what the history says happened.
          */}
          {entry.adminName ?? 'An administrator'} · {formatDateTime(entry.createdAt)}
        </p>
      </div>
    </li>
  )
}

function ActionIcon({ action }: { action: string | null }) {
  if (action === 'CANCELLED') return <CircleSlash />
  if (action === 'DRIVER_ASSIGNED') return <UserPlus />
  if (action === 'DRIVER_REASSIGNED') return <UserCog />
  return <CalendarClock />
}
